// ─────────────────────────────────────────────────────────────────────────────
// Network Service - TypeScript Implementation
// Simple network/internet reachability service with subscription support
// - Uses navigator.onLine + optional periodic probe to a lightweight endpoint
// ─────────────────────────────────────────────────────────────────────────────

import { INetworkService, NetworkState } from "../../types/services";

// Configuration constants
const DEFAULT_PING_URLS: string[] = [
  "https://www.cloudflare.com/",
  "https://www.bing.com/",
];

const DEFAULT_POLL_INTERVAL = 30000; // ms - configurable monitoring interval
const DEFAULT_TIMEOUT = 8000; // ms - timeout for each request
const DEFAULT_RETRY_COUNT = 2; // number of retries after initial failure
const DEFAULT_RETRY_DELAY = 1500; // ms - delay between retries

// Internal state management
type StateCallback = (state: NetworkState) => void;
type SimpleCallback = () => void;

const subscribers = new Set<StateCallback>();
let monitoring = false;
let intervalId: NodeJS.Timeout | null = null;
let onlineState = typeof navigator !== "undefined" ? !!navigator.onLine : true;
let lastChecked: Date | null = null;
let checking = false;
let consecutiveFailures = 0;
let currentPingUrl = "";

// Environment override for testing
const isForceOffline = process.env.REACT_APP_FORCE_OFFLINE === 'true';

// Internal helper functions
function getState(): NetworkState {
  return {
    online: isForceOffline ? false : onlineState,
    checking: checking,
    lastChecked,
    retryCount: consecutiveFailures,
  };
}

function notifySubscribers(): void {
  const state = getState();
  subscribers.forEach((callback) => {
    try {
      callback(state);
    } catch (error) {
      console.error("Error in network state subscriber:", error);
    }
  });
}

function updateState(online: boolean, wasChecked = true): void {
  const wasOnline = onlineState;
  const wasChecking = checking;

  onlineState = online;

  if (wasChecked) {
    lastChecked = new Date();
  }

  // Always notify subscribers of state changes
  if (wasOnline !== online || wasChecking !== checking) {
    notifySubscribers();
  }
}

async function testUrl(
  url: string,
  timeoutMs = DEFAULT_TIMEOUT
): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    await fetch(url, {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-cache",
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return true; // If we get here, we're online
  } catch (error: any) {
    if (error.name === "AbortError") {
      console.warn(`Network check timeout for ${url}`);
    }
    return false;
  }
}

async function checkWithRetry(
  urls: string[] = DEFAULT_PING_URLS,
  maxRetries = DEFAULT_RETRY_COUNT
): Promise<boolean> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Test multiple endpoints in parallel for better reliability
    const promises = urls.map((url) => testUrl(url));
    const results = await Promise.allSettled(promises);

    // If any endpoint responds, we're online
    const anyOnline = results.some(
      (result) => result.status === "fulfilled" && result.value === true
    );

    if (anyOnline) {
      consecutiveFailures = 0;
      currentPingUrl =
        urls[
          results.findIndex(
            (r) =>
              r.status === "fulfilled" &&
              (r as PromiseFulfilledResult<boolean>).value
          )
        ] || "";
      return true;
    }

    // If not the final attempt, wait before retrying
    if (attempt < maxRetries) {
      await new Promise((resolve) => setTimeout(resolve, DEFAULT_RETRY_DELAY));
      console.log(`Network check retry ${attempt + 1}/${maxRetries}`);
    }
  }

  consecutiveFailures++;
  return false;
}

// Public interface implementation
async function checkInternet(
  endpoints: string[] = DEFAULT_PING_URLS
): Promise<boolean> {
  if (checking) {
    return isForceOffline ? false : onlineState;
  }

  // If force offline is enabled, always return false
  if (isForceOffline) {
    updateState(false);
    return false;
  }

  checking = true;
  notifySubscribers(); // Notify that we started checking

  try {
    // Quick check: if navigator says we're offline, trust it initially
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      updateState(false);
      return false;
    }

    // Comprehensive check with multiple endpoints
    const isOnline = await checkWithRetry(endpoints);
    updateState(isOnline);

    return isOnline;
  } catch (error) {
    console.error("Network check failed:", error);
    // On error, assume offline (conservative approach)
    updateState(false);
    return false;
  } finally {
    checking = false;
    notifySubscribers(); // Notify that we finished checking
  }
}

function startMonitoring(
  options: {
    interval?: number;
    endpoints?: string[];
    retryAttempts?: number;
  } = {}
): void {
  if (monitoring) {
    return;
  }

  const interval = options.interval || DEFAULT_POLL_INTERVAL;
  const endpoints = options.endpoints || DEFAULT_PING_URLS;

  monitoring = true;

  checkInternet(endpoints).then(() => {
    notifySubscribers();
  });

  // Setup periodic monitoring
  intervalId = setInterval(() => {
    checkInternet(endpoints);
  }, interval);

  // Listen to browser online/offline events for immediate updates
  if (typeof window !== "undefined") {
    window.addEventListener("online", () => {
      checkInternet(endpoints); // Verify with actual connectivity test
    });

    window.addEventListener("offline", () => {
      updateState(false);
    });
  }
}

function stopMonitoring(): void {
  if (!monitoring) {
    return;
  }

  monitoring = false;

  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

function subscribe(callback: StateCallback): () => void {
  subscribers.add(callback);

  // Immediately notify new subscriber of current state
  setTimeout(() => callback(getState()), 0);

  // Return unsubscribe function
  return () => {
    subscribers.delete(callback);
  };
}

function onOnline(callback: SimpleCallback): () => void {
  return subscribe((state) => {
    if (state.online) {
      callback();
    }
  });
}

function onOffline(callback: SimpleCallback): () => void {
  return subscribe((state) => {
    if (!state.online) {
      callback();
    }
  });
}

function isOnline(): boolean {
  return isForceOffline ? false : onlineState;
}

function getConnectionInfo(): NetworkState & {
  monitoring: boolean;
  consecutiveFailures: number;
  currentPingUrl: string;
} {
  return {
    ...getState(),
    monitoring,
    consecutiveFailures,
    currentPingUrl,
  };
}

function forceCheck(): Promise<boolean> {
  return checkInternet();
}

// Service implementation that satisfies the interface
const networkService: INetworkService & {
  getConnectionInfo: () => NetworkState & {
    monitoring: boolean;
    consecutiveFailures: number;
    currentPingUrl: string;
  };
  forceCheck: () => Promise<boolean>;
  onOnline: (callback: SimpleCallback) => () => void;
  onOffline: (callback: SimpleCallback) => () => void;
} = {
  startMonitoring,
  stopMonitoring,
  checkInternet,
  subscribe,
  onOnline,
  onOffline,
  isOnline,
  getState,
  getConnectionInfo,
  forceCheck,
};

export default networkService;
